import { Injectable } from '@angular/core';

export const MAX_VIDEO_DURATION_SECONDS = 300; // 5 minutes limit

@Injectable({ providedIn: 'root' })
export class MediaValidatorService {

  /**
   * Reads video duration in seconds using browser HTMLVideoElement.
   */
  public getVideoDuration(file: File | Blob): Promise<number> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        resolve(0);
        return;
      }
      const video = document.createElement('video');
      video.preload = 'metadata';

      const cleanup = () => {
        if (video.src) {
          URL.revokeObjectURL(video.src);
        }
      };

      video.onloadedmetadata = () => {
        const duration = video.duration;
        cleanup();
        resolve(duration);
      };

      video.onerror = () => {
        cleanup();
        reject(new Error('Unable to read video metadata or invalid video file format.'));
      };

      video.src = URL.createObjectURL(file);
    });
  }

  /**
   * Validates if a video file exceeds the maximum allowed duration (default 5 mins / 300s).
   * Throws an error if invalid, or returns duration.
   */
  public async validateVideoDuration(file: File | Blob, maxSeconds = MAX_VIDEO_DURATION_SECONDS): Promise<number> {
    const duration = await this.getVideoDuration(file);
    if (duration > maxSeconds) {
      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);
      const formatted = `${minutes}m ${seconds}s`;
      throw new Error(`Video duration (${formatted}) exceeds the maximum allowed limit of 5 minutes.`);
    }
    return duration;
  }
}
